import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Typography, Table, Divider, Space, InputNumber, Radio } from "antd";
import dayjs from "dayjs";
import { UiSectionCard, UiButton } from "../../ui";
import IconChip from "../common/IconChip";
import CgIcon from "../common/CgIcon";
import DecimalInput from "../common/DecimalInput";
import styles from "./saleEntry.module.css";
import { formatAmount, formatMoney, formatRupees } from "../common/money";
import { gstPercent, gstSplit } from "../common/gstMath";

const CARD_ICON = {
  color: "var(--ant-primary-color)",
  background: "color-mix(in srgb, var(--ant-primary-color) 12%, white)",
};
const CardTitle = () => (
  <Space>
    <IconChip icon={<CgIcon name="billing" size={15} stroke={1.9} />} color={CARD_ICON.color} background={CARD_ICON.background} size="sm" />
    <span>Bill</span>
  </Space>
);

const receiptItemColumns = [
  {
    title: "Medicine",
    key: "medicine",
    render: (_, item) => item.pharmacy_medicine_name || item.medicine_name || item.name || "-",
  },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "center" },
  { title: "Rate", dataIndex: "rate", key: "rate", align: "right", render: (v) => formatAmount(v) },
  {
    title: "Discount",
    dataIndex: "discount",
    key: "discount",
    align: "right",
    render: (v) => (v ? `- ${formatAmount(v)}` : "-"),
  },
  { title: "GST", dataIndex: "gst_amount", key: "gst_amount", align: "right", render: (v) => formatAmount(v) },
  { title: "Amount", dataIndex: "amount", key: "amount", align: "right", render: (v) => formatAmount(v) },
];

/*
 * The heads a line's GST divides into, as the order states them.
 *
 * `pharmacy_medicine_gst_split` rides along with each item. Read as
 * percentages, under either spelling of the keys, and ignored where it says
 * nothing - a split of all noughts is a split nobody filled in, not a line
 * charged nothing.
 */
const readSplit = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const rate = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const split = {
    sgst: rate(raw.sgst ?? raw.SGST),
    cgst: rate(raw.cgst ?? raw.CGST),
    igst: rate(raw.igst ?? raw.IGST),
    cess: rate(raw.cess ?? raw.CESS),
  };
  return split.sgst || split.cgst || split.igst || split.cess ? split : null;
};

const buildPreview = (rawLines, discountMode, discountValue) => {
  if (!rawLines?.length) return null;

  const subtotal = rawLines.reduce((sum, l) => sum + l.lineAmount, 0);
  const lineDiscountTotal = rawLines.reduce((sum, l) => sum + l.lineDiscount, 0);
  const taxableBase = subtotal - lineDiscountTotal;

  const overallDiscount =
    discountMode === "percentage"
      ? taxableBase * ((discountValue || 0) / 100)
      : Math.min(discountValue || 0, taxableBase);

  let gstAmount = 0;
  const lines = rawLines.map((l) => {
    const taxableBeforeOverall = l.lineAmount - l.lineDiscount;
    const overallShare = taxableBase ? (taxableBeforeOverall / taxableBase) * overallDiscount : 0;
    const itemDiscount = l.lineDiscount + overallShare;
    const taxable = Math.max(l.lineAmount - itemDiscount, 0);

    /*
     * The rate charged, and the heads it is charged under.
     *
     * The split is the order's own; where it sends none, the line's single
     * percentage stands and is halved between SGST and CGST the way the
     * purchase screens halve theirs. Either way the two heads add back up to
     * the figure on the bill, so the summary and the total agree.
     */
    const split = readSplit(l.gstSplit);
    const percent = split ? gstPercent(split) : l.gstPercentage || 0;
    const heads = split
      ? gstSplit(split)
      : { sgst: percent / 2, cgst: percent / 2, igst: 0 };

    const lineGst = taxable * (percent / 100);
    gstAmount += lineGst;

    /* Cess, where the medicine carries one - the split's own figure if it
       sends one, otherwise the medicine's. Charged on the same taxable value
       as the GST beside it, and counted under its own head. */
    const cessPct =
      Number(readSplit(l.gstSplit)?.cess ?? l.cessPercentage ?? 0) || 0;
    const lineCess = taxable * (cessPct / 100);
    return {
      id: l.id,
      name: l.name,
      quantity: l.quantity,
      rate: l.rate,
      discount: itemDiscount,
      gst_amount: lineGst,
      amount: taxable + lineGst,
      /* What the GST summary slabs the line under, and the heads it prints
         it in - the order's own split where there is one. */
      gst_percent: percent,
      taxable_amount: taxable,
      sgst: (taxable * heads.sgst) / 100,
      cgst: (taxable * heads.cgst) / 100,
      igst: (taxable * heads.igst) / 100,
      cess: lineCess,
    };
  });

  const discount = lineDiscountTotal + overallDiscount;
  return {
    lines,
    subtotal,
    discount,
    lineDiscountTotal,
    overallDiscount,
    gstAmount,
    grandTotal: subtotal - discount + gstAmount,
  };
};

/**
 * The bill, laid out as the mockup lays it out: what the lines come to, what
 * was taken off them, the tax, and then the one figure the overall discount
 * acts on.
 *
 * `billTotal` is this same preview with the overall discount set to zero - the
 * bill as it stands with only the line discounts in it. It has to be computed
 * that way rather than as `subtotal - lineDiscount + gst`, because the server
 * takes the overall discount off before GST, so the tax moves when it is typed.
 * Settling the figure first is what lets the rows above the discount stay put
 * while it is being typed.
 */
const BillSummary = ({ preview, billTotal, discountControl, carriedBill }) => {
  /*
   * What was typed into the box above, in rupees - a percentage is shown as the
   * amount it comes to. Deliberately not the drop in the bill total: the server
   * discounts before GST, so taking one away also takes away the tax that was
   * sitting on it, and reporting that combined figure meant the line read -1,100
   * against a 1,000 that had just been entered. The tax it saves shows up in the
   * GST row, where it belongs.
   */
  const lessOverall = preview.overallDiscount;
  /*
   * A figure that changes should say so. Remounting on a new value is what
   * replays the flash; the first paint has nothing to have changed from.
   */
  const [bumpKey, setBumpKey] = useState(0);
  const lastTotalRef = useRef(null);
  useEffect(() => {
    if (lastTotalRef.current !== null && lastTotalRef.current !== preview.grandTotal) {
      setBumpKey((key) => key + 1);
    }
    lastTotalRef.current = preview.grandTotal;
  }, [preview.grandTotal]);

  return (
    <>
      <div className={styles.seSum}>
        {/*
          * What this customer already owes on the bill this sale is going
          * onto. It leads, above the subtotal, because it was owed before the
          * entry was started - and the Grand Total below has to come to the
          * whole bill, or the figure on screen is not the figure being asked
          * for at the counter.
          */}
        {carriedBill ? (
          <div>
            <span className={styles.k}>
              Already on this bill
              {carriedBill.number ? <em>{carriedBill.number}</em> : null}
            </span>
            <span className={`${styles.v} ${styles.tnum}`}>
              {formatRupees(carriedBill.amount)}
            </span>
          </div>
        ) : null}
        <div>
          <span className={styles.k}>{carriedBill ? "This sale" : "Subtotal"}</span>
          <span className={`${styles.v} ${styles.tnum}`}>{formatRupees(preview.subtotal)}</span>
        </div>
        <div>
          <span className={styles.k}>
            Item discount
            <em>entered on the lines</em>
          </span>
          <span className={`${styles.v} ${styles.neg} ${styles.tnum}`}>
            {preview.lineDiscountTotal ? "- " : ""}
            {formatRupees(preview.lineDiscountTotal)}
          </span>
        </div>
        <div>
          <span className={styles.k}>GST</span>
          <span className={`${styles.v} ${styles.tnum}`}>{formatRupees(preview.gstAmount)}</span>
        </div>
      </div>

      <div className={styles.seBill}>
        <span>Bill total</span>
        <b className={styles.tnum}>{formatRupees(billTotal)}</b>
      </div>

      {discountControl}

      {lessOverall > 0.004 && (
        <div className={styles.seOff}>
          <span>Less overall discount</span>
          <b className={styles.tnum}>- {formatRupees(lessOverall)}</b>
        </div>
      )}

      <div className={styles.seGrand}>
        <span>{carriedBill ? "Bill total due" : "Grand Total"}</span>
        <b key={bumpKey} className={`${styles.tnum} ${bumpKey ? styles.seBumped : ""}`}>
          {formatRupees(preview.grandTotal + (carriedBill?.amount || 0))}
        </b>
      </div>
    </>
  );
};

const TotalsRows = ({
  subtotal,
  discount,
  gst,
  grandTotal,
  discountLabel = "Discount",
  highlightTotal,
  carriedBill,
}) => (
  <>
    {/*
      * What is already on the bill this sale is being added to.
      *
      * It leads, above the subtotal, because it is money owed before this
      * entry was started - and the Grand Total underneath has to be the whole
      * bill, or the figure on the screen is not the figure being asked for.
      */}
    {carriedBill ? (
      <Row justify="space-between" style={{ padding: "4px 0" }}>
        <Typography.Text type="secondary">
          Already on {carriedBill.number || "this bill"}
        </Typography.Text>
        <Typography.Text>{formatAmount(carriedBill.amount)}</Typography.Text>
      </Row>
    ) : null}
    <Row justify="space-between" style={{ padding: "4px 0" }}>
      <Typography.Text type="secondary">
        {carriedBill ? "This sale" : "Subtotal"}
      </Typography.Text>
      <Typography.Text>{formatAmount(subtotal)}</Typography.Text>
    </Row>
    <Row justify="space-between" style={{ padding: "4px 0" }}>
      <Typography.Text type="secondary">{discountLabel}</Typography.Text>
      <Typography.Text type="danger">- {formatAmount(discount)}</Typography.Text>
    </Row>
    <Row justify="space-between" style={{ padding: "4px 0" }}>
      <Typography.Text type="secondary">GST</Typography.Text>
      <Typography.Text>{formatAmount(gst)}</Typography.Text>
    </Row>
    {highlightTotal ? (
      <Row
        justify="space-between"
        align="middle"
        style={{
          marginTop: 12,
          padding: "10px 12px",
          background: "color-mix(in srgb, var(--ant-primary-color) 12%, white)",
          borderRadius: 8,
        }}
      >
        <Typography.Text strong style={{ fontSize: 15 }}>
          Grand Total
        </Typography.Text>
        <Typography.Text strong style={{ fontSize: 18, color: "var(--ant-primary-color)" }}>
          {formatMoney(grandTotal)}
        </Typography.Text>
      </Row>
    ) : (
      <>
        <Divider dashed style={{ margin: "8px 0" }} />
        <Row justify="space-between">
          <Typography.Text strong style={{ fontSize: 16 }}>
            Grand Total
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 16 }}>
            {formatAmount(grandTotal)}
          </Typography.Text>
        </Row>
      </>
    )}
  </>
);

const DiscountControl = ({ discountMode, setDiscountMode, discountValue, setDiscountValue }) => (
  <div>
    <Typography.Text type="secondary">Overall Bill Discount</Typography.Text>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
      <Radio.Group value={discountMode} onChange={(e) => setDiscountMode(e.target.value)} buttonStyle="solid">
        <Radio.Button value="percentage">%</Radio.Button>
        <Radio.Button value="rupees">₹</Radio.Button>
      </Radio.Group>
      <InputNumber min={0} style={{ width: 100 }} value={discountValue} onChange={(v) => setDiscountValue(v ?? 0)} />
    </div>
  </div>
);

/** The same control as one of the mockup's own rows, for the narrow column. */
const InlineDiscountControl = ({ discountMode, setDiscountMode, discountValue, setDiscountValue }) => (
  <div className={styles.seDisc}>
    <label htmlFor="se-odisc">Overall bill discount</label>
    <span className={styles.seDmode}>
      <button
        type="button"
        className={discountMode === "percentage" ? styles.on : undefined}
        onClick={() => setDiscountMode("percentage")}
      >
        %
      </button>
      <button
        type="button"
        className={discountMode === "rupees" ? styles.on : undefined}
        onClick={() => setDiscountMode("rupees")}
      >
        ₹
      </button>
    </span>
    <DecimalInput
      id="se-odisc"
      className={`${styles.fi} ${styles.sm} ${styles.num}`}
      value={discountValue}
      onChange={setDiscountValue}
    />
  </div>
);

const discountStorageKey = (orderId) =>
  orderId ? `pharmacy:saleEntryDiscount:${orderId}` : null;

const readStoredDiscount = (orderId) => {
  const key = discountStorageKey(orderId);
  if (!key) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const value = Number(parsed?.discount);
    if (!Number.isFinite(value)) return null;
    return {
      mode: parsed?.discount_type === "rupees" ? "rupees" : "percentage",
      value,
    };
  } catch {
    return null;
  }
};

const SaleEntryBillingCard = forwardRef(({ order, bill, rawTotals, onBillRefresh, onPayableChange, onGstLinesChange, carriedBill, stacked }, ref) => {
  const navigate = useNavigate();
  const [discountMode, setDiscountMode] = useState(
    () => readStoredDiscount(order?.id)?.mode ?? "percentage"
  );
  const [discountValue, setDiscountValue] = useState(
    () => readStoredDiscount(order?.id)?.value ?? 0
  );
  const isNurseOrder = Boolean(order?.raised_by_nurse);
  /*
   * Whether this order has already been billed.
   *
   * `bill` holds one only when this page raised it during this visit - nothing
   * fetches the bill back when a billed order is opened again from the Orders
   * list, so on that second visit it is null and the card falls through to the
   * editable preview. The order itself remembers, in `bill_number`, and a
   * completed order has been dealt with whichever way it got there.
   *
   * It matters here because the overall discount is a figure that goes into
   * Generate Bill. Once the bill exists there is nothing left for it to act
   * on, and a box still offering to take one is a box offering to change a
   * total that was settled and printed.
   */
  const alreadyBilled =
    Boolean(bill) || Boolean(order?.bill_number) || order?.status === "completed";
  /*
   * An IP sale is not settled at the counter - it is submitted and lands on the
   * admission's own bill, which the ward settles later. So nothing on this
   * screen collects money for one: no dialog before the bill, and no payment
   * panel after it.
   */
  const isIpSale = (bill?.sale_type || order?.sale_type) === "IP";

  const restoredForOrderRef = useRef(null);
  const lineSignature = (rawTotals?.lines || [])
    .map((line) => `${line?.id ?? ""}:${line?.quantity ?? ""}`)
    .join("|");
  const discountLinesRef = useRef(null);

  useEffect(() => {
    const orderId = order?.id;
    if (!orderId || restoredForOrderRef.current === orderId) return;
    restoredForOrderRef.current = orderId;
    const stored = readStoredDiscount(orderId);
    if (!stored) return;
    if (discountLinesRef.current !== null) return;
    setDiscountMode(stored.mode);
    setDiscountValue(stored.value);
  }, [order?.id]);

  useEffect(() => {
    if (!order?.id) return;
    const previous = discountLinesRef.current;
    discountLinesRef.current = lineSignature;
    if (previous === null || previous === lineSignature) return;
    if (discountValue) {
      setDiscountValue(0);
      try {
        const key = discountStorageKey(order?.id);
        if (key) window.sessionStorage.removeItem(key);
      } catch {
      }
    }
  }, [lineSignature, order?.id]);


  useEffect(() => {
    const key = discountStorageKey(order?.id);
    if (!key) return;
    try {
      if (bill) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(
          key,
          JSON.stringify({ discount_type: discountMode, discount: discountValue || 0 })
        );
      }
    } catch {
    }
  }, [order?.id, bill, discountMode, discountValue]);

  /* What Generate Bill posts alongside the order: the server discounts before
     GST, which is why the preview above has to do the same. */
  useImperativeHandle(ref, () => ({
    getDiscount: () => ({ discount_type: discountMode, discount: discountValue || 0 }),
  }));

  const preview = useMemo(
    /* Nothing to type a discount into on a billed order, so nothing to
       preview one against either. */
    () => buildPreview(rawTotals?.lines, discountMode, alreadyBilled ? 0 : discountValue),
    [rawTotals, discountMode, discountValue, alreadyBilled]
  );

  /*
   * The lines as this card has worked them out, for the GST summary beside it.
   *
   * The summary needs each line's slab, its taxable value and the heads the
   * tax divides into - and all three are made here, in `buildPreview`, out of
   * the raw lines the grid reports. The screen was handing the summary those
   * raw lines instead, which carry none of it, so every row totalled nothing
   * and the table printed a single row of noughts.
   */
  const reportedGstRef = useRef("");
  useEffect(() => {
    if (!onGstLinesChange) return;
    const lines = preview?.lines || [];
    const signature = lines
      .map(
        (l) =>
          `${l.id}:${l.gst_percent}:${l.taxable_amount}:${l.sgst}:${l.cgst}:${l.igst}:${l.cess}`
      )
      .join("|");
    if (reportedGstRef.current === signature) return;
    reportedGstRef.current = signature;
    onGstLinesChange(lines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  /*
   * What the action bar shows as Payable. The grand total is worked out here -
   * it is the only place that knows the overall discount - so the bar is told
   * rather than recomputing it and risking the two disagreeing.
   */
  /*
   * What the counter is actually collecting. A sale added to an existing bill
   * is paid for together with what was already on it, so the bar shows the
   * bill, not this entry's share of it.
   */
  const payable = bill
    ? Number(bill.grand_total || 0)
    : (preview?.grandTotal ?? 0) + (carriedBill?.amount || 0);
  const lineCount = bill ? (bill.items || []).length : preview?.lines?.length ?? 0;
  useEffect(() => {
    onPayableChange?.({ amount: payable, lines: lineCount, billed: Boolean(bill) });
  }, [payable, lineCount, bill]);

  /*
   * What the lines come to with the tax that is actually due on them - the
   * figure the overall discount is taken off.
   *
   * Read off the same preview as every other row, so the card reconciles: bill
   * total less the discount is the grand total, exactly. It does shift by the
   * tax on the discount as that is typed, because the server charges GST on
   * what is left after it - the alternative was a column of figures that did
   * not add up to the total printed under them.
   */
  const billTotal = preview
    ? preview.subtotal - preview.lineDiscountTotal + preview.gstAmount
    : 0;


  const discountProps = {
    discountMode,
    setDiscountMode,
    discountValue,
    setDiscountValue,
  };
  const discountControl = alreadyBilled ? null : <DiscountControl {...discountProps} />;
  const inlineDiscountControl = alreadyBilled ? null : (
    <InlineDiscountControl {...discountProps} />
  );

  /* The mockup's own Billing card, for the 30% column beside the line grid. */
  const billingCard = preview ? (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>Billing</h3>
      </div>
      <BillSummary
        preview={preview}
        billTotal={billTotal}
        discountControl={inlineDiscountControl}
        carriedBill={carriedBill}
      />
    </section>
  ) : null;
  if (isNurseOrder) {
    if (!bill) {
      if (!preview?.lines?.length) {
        return (
          <UiSectionCard title={<CardTitle />}>
            <Typography.Text type="secondary">
              {order?.id
                ? "Add medicines above, then Generate Bill to dispense and bill this order."
                : "Fill patient details and add medicines to generate a bill."}
            </Typography.Text>
          </UiSectionCard>
        );
      }

      /* Beside the line grid the lines are already on screen, so the narrow
         column carries the bill alone rather than repeating them in a table
         that has no room to breathe. */
      if (stacked) return billingCard;

      return (
        <UiSectionCard title={<CardTitle />}>
          <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 16 }}>
            <Table
              dataSource={preview.lines}
              columns={receiptItemColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: "max-content" }}
            />

            <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
              {/* An empty column where the control used to be would push the
                  totals back to the middle of the row. */}
              {discountControl ? <Col>{discountControl}</Col> : null}
              <Col span={10}>
                <TotalsRows
                  subtotal={preview.subtotal}
                  discount={preview.discount}
                  gst={preview.gstAmount}
                  grandTotal={preview.grandTotal}
                  discountLabel="Discount (items + overall)"
                />
              </Col>
            </Row>
          </div>

          <Typography.Text type="secondary" style={{ display: "block", marginTop: 12 }}>
            Preview only - click Generate Bill to dispense these items and issue the final bill.
          </Typography.Text>
        </UiSectionCard>
      );
    }

    return (
      <UiSectionCard title={<CardTitle />}>
        <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 16 }}>
          <BillHeader bill={bill} />
          {!stacked && (
            <Table
              dataSource={bill.items || []}
              columns={receiptItemColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: "max-content" }}
            />
          )}
          <Row justify="end" style={{ marginTop: 16 }}>
            <Col span={stacked ? 24 : 10}>
              <TotalsRows
                subtotal={bill.subtotal}
                discount={bill.discount}
                gst={bill.gst_amount}
                grandTotal={bill.grand_total}
              />
            </Col>
          </Row>
          <BillFooterNote />
        </div>

        <UiButton style={{ marginTop: 16 }} onClick={() => navigate(`/pharmacy/billing/${bill.id}`)}>
          {isIpSale ? "View Full Bill" : "View Bill / Record Payment"}
        </UiButton>
      </UiSectionCard>
    );
  }

  if (!bill) {
    if (!preview?.lines?.length) {
      return (
        <UiSectionCard title={<CardTitle />}>
          <Typography.Text type="secondary">
            {order?.id
              ? "Add medicines above, then Generate Bill to dispense and bill this order."
              : "Fill patient details and add medicines to generate a bill."}
          </Typography.Text>
        </UiSectionCard>
      );
    }

    /* Beside the line grid the column carries the bill and nothing else: the
       money is collected in the Record Payment dialog the action bar opens, not
       in a panel under the totals. */
    return billingCard;
  }

  const billCard = (
    <UiSectionCard title={<CardTitle />}>
      <BillHeader bill={bill} />
      <TotalsRows
        subtotal={bill.subtotal}
        discount={bill.discount}
        gst={bill.gst_amount}
        grandTotal={bill.grand_total}
        highlightTotal
      />
      <BillFooterNote />
      <UiButton
        variant="text"
        style={{ marginTop: 12, paddingInline: 0 }}
        onClick={() => navigate(`/pharmacy/billing/${bill.id}`)}
      >
        View Full Bill
      </UiButton>
    </UiSectionCard>
  );

  /*
   * The bill, and nothing that collects against it.
   *
   * Money is taken in the Record Payment dialog the action bar opens, or later
   * from Billing if the bill was raised to be settled another time. A receipt
   * panel here was a third way to do the same thing, sitting under a bill that
   * had just been paid for - and for an IP sale, which the ward settles, it
   * offered to collect money that is never collected at this counter.
   */
  return billCard;
});

const BillHeader = ({ bill }) => (
  <>
    <Row justify="space-between">
      <Col>
        <Typography.Text strong>Pharmacy Bill</Typography.Text>
      </Col>
      <Col style={{ textAlign: "right" }}>
        <div>
          <Typography.Text type="secondary">Invoice No.</Typography.Text> {bill.bill_number}
        </div>
        <div>
          <Typography.Text type="secondary">Date</Typography.Text> {dayjs(bill.created_at).format("DD MMM YYYY")}
        </div>
      </Col>
    </Row>

    <Divider dashed style={{ margin: "12px 0" }} />

    <Row gutter={16}>
      <Col span={8}>
        <Typography.Text type="secondary">Patient</Typography.Text>
        <div>{bill.patient_name || bill.counter_sale_customer_name || "-"}</div>
      </Col>
      <Col span={8}>
        <Typography.Text type="secondary">Order Number</Typography.Text>
        <div>{bill.order_number}</div>
      </Col>
      <Col span={8}>
        <Typography.Text type="secondary">Sale Type</Typography.Text>
        <div>{bill.sale_type}</div>
      </Col>
    </Row>

    <Divider style={{ margin: "12px 0" }} />
  </>
);

const BillFooterNote = () => (
  <div style={{ marginTop: 16, background: "#fafafa", padding: 8, borderRadius: 4 }}>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      Thank you. Medicines once dispensed are non-returnable except as per pharmacy return policy.
    </Typography.Text>
  </div>
);

export default SaleEntryBillingCard;
